import { useEffect, useMemo, useRef } from 'react'
import type { TripItineraryData, TripDay } from 'tripPlanner/api/types'
import type { TripPlannerEvent } from '../contexts/TripPlannerEventBus'
import { useEventSubscription } from '../contexts/TripPlannerEventBus'

export function computeItineraryFingerprint(data: TripItineraryData | undefined): string | undefined {
  if (!data?.stops) return undefined

  return JSON.stringify({
    stopCount: data.stops.length,
    stopIds: data.stops.map(s => s.id),
    itemCounts: data.stops.map(s =>
      s.days.reduce((acc: number, d: TripDay) => acc + d.items.length, 0) +
      (s.itemsWithoutDay?.length ?? 0),
    ),
  })
}

export function useItineraryFingerprint(args: {
  itineraryData: TripItineraryData | undefined
  hasItineraryData: boolean
  dispatch: (event: TripPlannerEvent) => void
}): void {
  const { itineraryData, hasItineraryData, dispatch } = args

  const prevFingerprintRef = useRef<string | undefined>(undefined)
  const prevHasItineraryRef = useRef(false)

  const fingerprint = useMemo(
    () => computeItineraryFingerprint(itineraryData),
    [itineraryData],
  )

  useEffect(() => {
    const previousFingerprint = prevFingerprintRef.current
    const itineraryChanged = previousFingerprint !== undefined && fingerprint !== previousFingerprint
    const itineraryAppeared = hasItineraryData && !prevHasItineraryRef.current

    if (itineraryAppeared || itineraryChanged) {
      dispatch({ type: 'itinerary_changed' })
    }

    prevHasItineraryRef.current = hasItineraryData
    prevFingerprintRef.current = fingerprint
  }, [hasItineraryData, fingerprint, dispatch])

  useEventSubscription('reset_chat_state', () => {
    prevFingerprintRef.current = undefined
    prevHasItineraryRef.current = false
  })
}
